import {
  getPermissionScope,
  hasPermission,
  PermissionKey,
  type PermissionMap,
  PermissionScope,
  type PermissionScopeEnum,
  TeamMemberRole,
} from "@fitness-app/data-models";
import { type TeamMemberRoleEnum } from "@fitness-app/data-models/entities/TeamMember";

import { type SalesCrmTabKey, salesCrmTabKeys } from "./salesCrmTabs";

export interface SalesCrmAccess {
  canInspectAgentChats: boolean;
  canManageBlacklist: boolean;
  canManageAgentSkills: boolean;
  canManageSettlementConfiguration: boolean;
  canManageSales: boolean;
  canManageSalesGlobally: boolean;
  canReadSales: boolean;
  canReadSettlements: boolean;
  canSelectSalesSeller: boolean;
  canUseSalesAgent: boolean;
  memberId: string | null;
  salesManageScope: PermissionScopeEnum;
  salesReadScope: PermissionScopeEnum;
  /** True for a seller who only ever gets their own settlement row. */
  canReadOwnSettlements: boolean;
  visibleTabs: readonly SalesCrmTabKey[];
}

export interface SalesMeetingCapabilities {
  canBlacklist: boolean;
  canUpdate: boolean;
}

interface SalesCrmCapabilities {
  canManageSales: boolean;
  canManageSalesGlobally: boolean;
  canReadSales: boolean;
  canReadSalesGlobally: boolean;
  canReadSettlements: boolean;
  canReadOwnSettlements: boolean;
  canUseAgents: boolean;
}

const salesCrmTabVisibility: Record<SalesCrmTabKey, (capabilities: SalesCrmCapabilities) => boolean> = {
  dashboard: ({ canReadSales }) => canReadSales,
  meetings: ({ canReadSales }) => canReadSales,
  agent: ({ canReadSales, canUseAgents }) => canReadSales && canUseAgents,
  // Odczyt szablonów w bazie wisi na sales.read (dowolny scope); edycję i tak
  // gate'uje sales.manage ALL — sprzedawca dostaje podgląd, nie CRUD.
  templates: ({ canReadSales, canManageSalesGlobally }) => canReadSales || canManageSalesGlobally,
  "call-analysis": ({ canManageSalesGlobally, canReadSales, canUseAgents }) =>
    canManageSalesGlobally && canReadSales && canUseAgents,
  // Zarządzanie sprzedawcami to operacja tenantowa — sales.manage ASSIGNED
  // (baseline SALES_REP, do edycji własnych spotkań) nie może otwierać listy.
  sellers: ({ canManageSalesGlobally }) => canManageSalesGlobally,
  // Lustro polityki RLS: odczyt blacklisty wymaga sales.read ALL, zapisy
  // sales.manage ALL — sprzedawca widzi listę, nie edytuje.
  blacklist: ({ canReadSalesGlobally, canManageSalesGlobally }) => canReadSalesGlobally || canManageSalesGlobally,
  // Mirrors the RPC gate: sales_crm_monthly_settlement* answers a tenant-wide
  // reader OR a seller asking for their own row (TRE-1827). Gating the tab on the
  // permission alone hid it from every SALES_REP, whose baseline has none.
  settlements: ({ canReadSettlements, canReadOwnSettlements }) => canReadSettlements || canReadOwnSettlements,
};

export const createSalesCrmAccess = (
  permissions: PermissionMap,
  memberId?: string | null,
  memberRole?: TeamMemberRoleEnum | null,
): SalesCrmAccess => {
  const salesReadScope = getPermissionScope(permissions, PermissionKey.SALES_READ);
  const salesManageScope = getPermissionScope(permissions, PermissionKey.SALES_MANAGE);
  const capabilities = {
    canReadSales: hasPermission(permissions, PermissionKey.SALES_READ),
    canReadSalesGlobally: salesReadScope === PermissionScope.ALL,
    canManageSales: hasPermission(permissions, PermissionKey.SALES_MANAGE),
    canManageSalesGlobally: salesManageScope === PermissionScope.ALL,
    canReadSettlements: hasPermission(permissions, PermissionKey.SALES_SETTLEMENTS_READ, PermissionScope.ALL),
    canReadOwnSettlements: memberRole === TeamMemberRole.SALES_REP,
    canUseAgents: hasPermission(permissions, PermissionKey.AGENTS_USE),
  };

  return {
    canInspectAgentChats:
      capabilities.canUseAgents &&
      salesManageScope === PermissionScope.ALL &&
      getPermissionScope(permissions, PermissionKey.CHAT_READ) === PermissionScope.ALL,
    // Blokowanie osoby to codzienna operacja sprzedaży, nie zarządzanie kontem —
    // polityki na sales_booking_blacklist pytają o sales.manage w dowolnym
    // zakresie, więc sprzedawca (ASSIGNED) też blokuje.
    canManageBlacklist: capabilities.canManageSales,
    canManageAgentSkills:
      salesManageScope === PermissionScope.ALL &&
      getPermissionScope(permissions, PermissionKey.AGENTS_USE) === PermissionScope.ALL,
    canManageSettlementConfiguration:
      capabilities.canManageSales &&
      hasPermission(permissions, PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL),
    canManageSales: capabilities.canManageSales,
    canManageSalesGlobally: capabilities.canManageSalesGlobally,
    canReadSales: capabilities.canReadSales,
    canReadSettlements: capabilities.canReadSettlements,
    canReadOwnSettlements: capabilities.canReadOwnSettlements,
    // Sprzedawca (SALES_REP) ma sales.read ALL tylko dla pełnej listy klientek —
    // spotkania są scope'owane do własnych (RLS: sales_crm_can_access_meeting),
    // więc wybór innego sprzedawcy nie może być dostępny z samego scope'u.
    canSelectSalesSeller:
      memberRole !== TeamMemberRole.SALES_REP &&
      (salesReadScope === PermissionScope.TEAM || salesReadScope === PermissionScope.ALL),
    canUseSalesAgent: capabilities.canReadSales && capabilities.canUseAgents,
    memberId: memberId ?? null,
    salesManageScope,
    salesReadScope,
    visibleTabs: salesCrmTabKeys.filter((tab) => salesCrmTabVisibility[tab](capabilities)),
  };
};

export const resolveVisibleSalesCrmTab = (
  requestedTab: SalesCrmTabKey,
  access: SalesCrmAccess,
): SalesCrmTabKey | null => {
  if (access.visibleTabs.includes(requestedTab)) {
    return requestedTab;
  }

  return access.visibleTabs[0] ?? null;
};

export const getSalesMeetingSellerId = (access: SalesCrmAccess, selectedSellerId: string | null): string | null => {
  if (!access.canReadSales) {
    return null;
  }

  // Bez prawa wyboru sprzedawcy zapytania zawsze schodzą do własnego kalendarza
  // (m.in. SALES_REP, któremu RLS i tak nie odda cudzych spotkań).
  if (access.salesReadScope === PermissionScope.ASSIGNED || !access.canSelectSalesSeller) {
    return access.memberId;
  }

  return selectedSellerId;
};

export const canManageSalesSeller = (
  access: SalesCrmAccess,
  targetMemberId: string | null,
  assignedMemberIds: ReadonlySet<string>,
): boolean => {
  if (!access.canManageSales || !targetMemberId) {
    return false;
  }

  if (access.salesManageScope === PermissionScope.ALL) {
    return true;
  }

  if (targetMemberId === access.memberId) {
    return true;
  }

  return access.salesManageScope === PermissionScope.TEAM && assignedMemberIds.has(targetMemberId);
};

export const getSalesMeetingCapabilities = (
  access: SalesCrmAccess,
  targetMemberId: string | null,
  assignedMemberIds: ReadonlySet<string>,
): SalesMeetingCapabilities => {
  const canUpdate = canManageSalesSeller(access, targetMemberId, assignedMemberIds);

  return {
    canBlacklist: canUpdate && access.canManageBlacklist,
    canUpdate,
  };
};
